<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import type { BaseInput, BaseSelect } from '~/shared/model'
const route = useRoute()

const DEBUG_ADAPTER = false // Set to true to enable console logging

const props = defineProps<{
  context: FormKitFrameworkContext & BaseSelect
}>()
const name = props.context.nameValue as string
const rootState = computed(() => props.context.node.at('$root').value)
const rootFormKey = computed(() => props.context.node.at('$root')?.uid)
const Component = markRaw(props.context.attrs?.props?.component)
const disabled = ref(!!props?.context?.disabled)
let watcher = props.context.watcher as ReturnType<typeof useWatcher>

// Some selects are intentionally pinned to a fixed channel - e.g. the product
// category pickers on the General tab, which edit channel-1 assignments.
// Those selects opt out of
// the route channel coercion so their options are not filtered by the
// ?channel= query parameter (which would hide and then flush saved values).
const ignoreRouteChannel = props.context.ignoreRouteChannel === true

function coerceChannelWhere(input: any, routeChannel: number | null) {
  if (ignoreRouteChannel) return input
  if (!input || routeChannel == null || Number.isNaN(routeChannel)) return input
  try {
    const w =
      typeof input === 'object' ? JSON.parse(JSON.stringify(input)) : input
    if (
      w &&
      w.channelId &&
      typeof w.channelId === 'object' &&
      w.channelId.equals !== undefined
    ) {
      // Make sure we're setting a number, not an object
      w.channelId.equals = Number(routeChannel)
    }
    return w
  } catch (_) {
    return input
  }
}

const initialWhere = props.context.where
  ? props.context.where(rootState.value)
  : {}
const where = ref(coerceChannelWhere(initialWhere, Number(route.query.channel)))
if (DEBUG_ADAPTER)
  console.info('[Adapter] init where for', name, JSON.stringify(where.value))

if (!watcher && props.context.watch) {
  watcher = useWatcher(props.context.watch)
  watcher.bindState(rootState)
  if (DEBUG_ADAPTER)
    console.info(
      '[Adapter] watcher bound for',
      name,
      'with keys',
      props.context.watch,
    )
}
const componentRef = ref(null)

const model = computed({
  get() {
    return props?.context?.value
  },
  set(v) {
    props.context.node.input(v === false ? false : v || null)
  },
})

const flushValue = () => {
  if (model.value) {
    const value = JSON.stringify(model.value)
    if (DEBUG_ADAPTER) console.info(`[Adapter] Flushing ${name}:${value}`)
  }
  componentRef.value?.clearSelectOptions()
  if (model.value) {
    model.value = props.context.mode === 'single' ? null : []
  }
}

const checkForDisabledUpdates = (state?) => {
  if (props.context?.attrs?.watchDisabled) {
    const shouldDisable = props.context.attrs.watchDisabled(
      state || props.context.node.at('$root')?.value,
    )
    // if (shouldDisable && model.value) flushValue()
    nextTick(() => {
      disabled.value = shouldDisable
    })
  }
}

checkForDisabledUpdates()

if (watcher) {
  if (!props.context.keepValuesOnWatch) {
    watcher.addHandler('flushOnChange', async () => flushValue())
  }
  if (props.context.keepValuesOnWatch) {
    watcher.addHandler('flushOnEmpty', (current) => {
      if (
        Object.values(current).every(
          (v) => !v || (Array.isArray(v) ? !v.length : true),
        )
      ) {
        flushValue()
      }
    })
  }

  watcher.addHandler('default', async (current, previous, state: Ref<any>) => {
    checkForDisabledUpdates(state.value)
    if (props.context.where) {
      where.value = props.context.where(rootState.value)
      if (DEBUG_ADAPTER)
        console.info(
          '[Adapter] where updated for',
          name,
          JSON.stringify(where.value),
        )
    }
  })
}

// Re-bind watcher when the form root object changes (e.g., switching channel group or remount)
watch([rootState, rootFormKey], () => {
  if (watcher) watcher.bindState(rootState)
  if (DEBUG_ADAPTER) console.info('[Adapter] rootState changed for', name)
  if (props.context.where) {
    const state = (rootState.value || {}) as any
    const channel = Number(route.query.channel)
    const merged = ignoreRouteChannel
      ? state
      : {
          ...state,
          productChannel: {
            ...(state.productChannel || {}),
            channelId: Number.isNaN(channel)
              ? state?.productChannel?.channelId
              : channel,
          },
        }
    const computedWhere = props.context.where(merged)
    where.value = coerceChannelWhere(computedWhere, channel)
    if (DEBUG_ADAPTER)
      console.info(
        '[Adapter] where recomputed on root change for',
        name,
        JSON.stringify(where.value),
      )
  }
  // Proactively refresh options with the new where
  nextTick(() => componentRef.value?.resolveSelectOptions?.())
})

// Also react to route channel query changes to recompute where for channel-scoped selects
watch(
  () => route.query.channel,
  () => {
    if (DEBUG_ADAPTER)
      console.info(
        '[Adapter] route.channel changed for',
        name,
        route.query.channel,
      )
    if (props.context.where && !ignoreRouteChannel) {
      const state = (rootState.value || {}) as any
      const channel = Number(route.query.channel)
      const merged = {
        ...state,
        productChannel: {
          ...(state.productChannel || {}),
          channelId: Number.isNaN(channel)
            ? state?.productChannel?.channelId
            : channel,
        },
      }
      const computedWhere = props.context.where(merged)
      where.value = coerceChannelWhere(computedWhere, channel)
      if (DEBUG_ADAPTER)
        console.info(
          '[Adapter] where recomputed on route channel change for',
          name,
          JSON.stringify(where.value),
        )
    }
    nextTick(() => componentRef.value?.resolveSelectOptions?.())
  },
)
</script>
<template>
  <component
    :is="Component"
    ref="componentRef"
    v-model="model"
    :attrs="props.context.attrs.attrs"
    :canClear="props.context.canClear"
    :canDeselect="props.context.canDeselect"
    :caret="props.context.caret"
    :delay="props.context.delay"
    :disabled="disabled"
    :filterResults="props.context.filterResults"
    :formkit="true"
    :initialOptions="props.context.initialOptions"
    :keepValuesOnWatch="props.context.keepValuesOnWatch"
    :label="props.context.label"
    :mode="props.context.mode"
    :object="props.context.object"
    :options="props.context.options"
    :placeholder="props.context.placeholder"
    :resolveOnLoad="props.context.resolveOnLoad"
    :searchable="props.context.searchable"
    :showAddAll="props.context.attrs.showAddAll"
    :size="props.context.size"
    :view="props.context.view"
    :where="where"
  >
  </component>
</template>
